import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';

import { filter, first, switchMap, withLatestFrom } from 'rxjs/operators';
import { Observable } from 'rxjs';

import {
  getTenantId,
  hasSelectedEdgeExtension,
  isLiveStreamEnabled,
  isUserDetailsLoaded,
  selectCurrentAccountId,
  selectedEdgeExtension,
  selectedEdgeExtensionUniqueId
} from '@web/core/selectors';
import * as SideMenuActions from '@web/core/actions/side-menu.actions';
import * as EdgeExtensionsActions from '@web/core/actions/edge-extensions.actions';
import * as GalleryActions from '@web/gallery/core/actions/gallery.actions';

import { LocalStorageService } from '@common/modules/shared/services/local-storage.service';
import { StorageCacheKey } from '@common/modules/shared/interfaces';
import { FeatureSwitchService } from '@common/modules/core/services/feature-switch/feature-switch.service';
import { FeatureSwitch, SideMenuOption } from '@common/modules/core/services/interfaces';

@Injectable()
export abstract class BaseExtensionNavigationGuard {
  constructor(
    protected localStorageService: LocalStorageService,
    protected router: Router,
    protected store: Store,
    protected featureSwitchService: FeatureSwitchService
  ) {}

  public canActivate(): Observable<boolean> {
    if (!this.featureSwitchService.featureSwitch(FeatureSwitch.LiveEdge)) {
      return this.handleNoExtensionNavigation();
    }
    return this.waitForUserDetailsToLoad().pipe(switchMap(() => this.getTenantIdAndNavigate()));
  }

  protected abstract handleNoExtensionNavigation(): Observable<boolean>;

  protected waitForUserDetailsToLoad(): Observable<boolean> {
    return this.store.select(isUserDetailsLoaded).pipe(
      filter(loaded => loaded),
      first()
    );
  }

  protected getTenantIdAndNavigate(): Observable<boolean> {
    return this.store.select(getTenantId).pipe(
      first(),
      switchMap(tenantId => this.navigateToLastExtensionIfNeeded(tenantId))
    );
  }

  protected navigateToLastExtensionIfNeeded(tenantId: string): Observable<boolean> {
    const lastExtensionId = this.localStorageService.getItem(`${StorageCacheKey.LastExtension}_${tenantId}`)?.split('|')[0];

    // If invitation link, clear last extension and navigate accordingly
    if (this.isInvitationLink()) {
      return this.handleNoExtensionNavigation();
    }

    if (lastExtensionId) {
      return this.store.select(hasSelectedEdgeExtension).pipe(
        filter(loaded => loaded),
        first(),
        withLatestFrom(
          this.store.select(selectCurrentAccountId),
          this.store.select(selectedEdgeExtensionUniqueId),
          this.store.select(selectedEdgeExtension),
          this.store.select(isLiveStreamEnabled)
        ),
        switchMap(([_, accountId, edgeExtensionUniqueId, selectedEdgeExtension, isLiveEnabled]) => {
          if (selectedEdgeExtension && isLiveEnabled) {
            this.store.dispatch(SideMenuActions.sideMenuInitOption({ option: SideMenuOption.Media }));
            return this.router.navigate([`accounts/${accountId}/extensions/${edgeExtensionUniqueId}`]).then(() => {
              this.onNavigationSuccess();
              return true;
            });
          }
          return this.handleNoExtensionNavigation();
        })
      );
    }
    return this.handleNoExtensionNavigation();
  }

  protected isInvitationLink(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has('returnUrl');
  }

  protected onNavigationSuccess(): void {
    this.store.dispatch(EdgeExtensionsActions.setIsNavigationInProgress({ isNavigatingToEdgeExtension: false }));
    this.store.dispatch(GalleryActions.clearGallery());
  }
}
